import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
  runInAction,
} from "mobx";
import { DateTime } from "luxon";
import {
  FeatureLimit,
  type FeatureLimits,
  GatedFeature,
  GatedFeatureConfig,
} from "@/store/feature-gates/types";
import { type FeatureGateLevel, PaywallContext } from "@/domains/metrics/tracked-event";
import { buildFeatureGateConfigFromData, getPeriodWindow } from "@/store/feature-gates/utils";
import { GATED_FEATURE_DEFAULTS, UNKNOWN_GATE_FALLBACK } from "@/store/feature-gates/defaults";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { liveQuery, Subscription } from "dexie";
import { logger } from "@/modules/logger";
import { SubscriptionPlanKind } from "@/store/subscriptions/types";
import type { FeatureAccessObservable } from "@/store/subscriptions/FeatureAccessObservable";

/** The counter normally emits within milliseconds; this long means stranded. */
const NATIVE_CHAT_COUNTER_WATCHDOG_MS = 10_000;

export class AppStoreFeatureGatesStore extends AppSubStore {
  upsellBannerVisible: boolean = false;
  upsellBannerExceeded: boolean = false;

  private noteCreationCounterSubscription?: Subscription;
  private chatMessageCounterSubscription?: Subscription;
  private nativeChatMessageCounterSubscription?: Subscription;
  private nativeChatMessageCounterWatchdogTimer?: ReturnType<typeof setTimeout>;
  private pdfPagesProcessedCounterSubscription?: Subscription;
  private _noteCreationCount: number = 0;
  private _chatMessageCount: number = 0;
  private _nativeChatMessageCount: number = 0;
  private _nativeChatMessageCountLoaded: boolean = false;
  private _pdfPagesProcessedCount: number = 0;
  private _currentPeriodStart: DateTime = DateTime.local().startOf("month");

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      AppStoreFeatureGatesStore,
      | "getGateConfig"
      | "resolveFeatureLimitKey"
      | "subscribeToNoteCreationCounter"
      | "unsubscribeFromNoteCreationCounter"
      | "subscribeToChatMessageCounter"
      | "unsubscribeFromChatMessageCounter"
      | "subscribeToNativeChatMessageCounter"
      | "unsubscribeFromNativeChatMessageCounter"
      | "subscribeToPdfPagesProcessedCounter"
      | "unsubscribeFromPdfPagesProcessedCounter"
      | "noteCreationCounterSubscription"
      | "chatMessageCounterSubscription"
      | "nativeChatMessageCounterSubscription"
      | "nativeChatMessageCounterWatchdogTimer"
      | "pdfPagesProcessedCounterSubscription"
      | "_noteCreationCount"
      | "_chatMessageCount"
      | "_nativeChatMessageCount"
      | "_nativeChatMessageCountLoaded"
      | "_pdfPagesProcessedCount"
      | "_currentPeriodStart"
    >(this, {
      useInitializeAppStoreEffects: false,
      openNoteCreationUpsell: false,
      openChatMessageUpsell: false,
      checkGate: false,
      openUsageLimitUpsell: action,
      closeUpsell: action,
      getGateConfig: false,
      resolveFeatureLimitKey: false,
      upsellBannerVisible: observable,
      upsellBannerExceeded: observable,
      refreshUpsellBanner: action,
      noteCreationCount: computed,
      chatMessageCount: computed,
      nativeChatMessageCount: computed,
      nativeChatMessageCountLoaded: computed,
      pdfPagesProcessedCount: computed,
      currentPeriodStart: computed,
      subscribeToNoteCreationCounter: action,
      unsubscribeFromNoteCreationCounter: action,
      subscribeToChatMessageCounter: action,
      unsubscribeFromChatMessageCounter: action,
      subscribeToNativeChatMessageCounter: action,
      unsubscribeFromNativeChatMessageCounter: action,
      subscribeToPdfPagesProcessedCounter: action,
      unsubscribeFromPdfPagesProcessedCounter: action,
      noteCreationCounterSubscription: false,
      chatMessageCounterSubscription: false,
      nativeChatMessageCounterSubscription: false,
      nativeChatMessageCounterWatchdogTimer: false,
      pdfPagesProcessedCounterSubscription: false,
      _noteCreationCount: observable,
      _chatMessageCount: observable,
      _nativeChatMessageCount: observable,
      _nativeChatMessageCountLoaded: observable,
      _pdfPagesProcessedCount: observable,
      _currentPeriodStart: observable,
    });

    onBecomeObserved(this, "noteCreationCount", () => this.subscribeToNoteCreationCounter());
    onBecomeUnobserved(this, "noteCreationCount", () => this.unsubscribeFromNoteCreationCounter());
    onBecomeObserved(this, "chatMessageCount", () => this.subscribeToChatMessageCounter());
    onBecomeUnobserved(this, "chatMessageCount", () => this.unsubscribeFromChatMessageCounter());
    onBecomeObserved(this, "nativeChatMessageCount", () =>
      this.subscribeToNativeChatMessageCounter()
    );
    onBecomeUnobserved(this, "nativeChatMessageCount", () =>
      this.unsubscribeFromNativeChatMessageCounter()
    );
    onBecomeObserved(this, "pdfPagesProcessedCount", () =>
      this.subscribeToPdfPagesProcessedCounter()
    );
    onBecomeUnobserved(this, "pdfPagesProcessedCount", () =>
      this.unsubscribeFromPdfPagesProcessedCounter()
    );
  }

  get currentPeriodStart(): DateTime {
    return this._currentPeriodStart;
  }

  get noteCreationCount(): number {
    return this._noteCreationCount;
  }

  get chatMessageCount(): number {
    return this._chatMessageCount;
  }

  get nativeChatMessageCount(): number {
    return this._nativeChatMessageCount;
  }

  get nativeChatMessageCountLoaded(): boolean {
    return this._nativeChatMessageCountLoaded;
  }

  get pdfPagesProcessedCount(): number {
    return this._pdfPagesProcessedCount;
  }

  subscribeToNoteCreationCounter = () => {
    this.unsubscribeFromNoteCreationCounter();
    const periodStart = DateTime.local().startOf("month");
    this._currentPeriodStart = periodStart;

    this.noteCreationCounterSubscription = liveQuery(async () => {
      // liveQuery tracks Dexie queries made within async functions, so calling
      // countMyNotesCreatedSince works correctly and will react to database changes
      return await this.store.notes.countMyNotesCreatedSince(periodStart);
    }).subscribe({
      next: count => {
        runInAction(() => {
          this._noteCreationCount = count;
        });
      },
    });
  };

  unsubscribeFromNoteCreationCounter = () => {
    this.noteCreationCounterSubscription?.unsubscribe();
    this.noteCreationCounterSubscription = undefined;
  };

  subscribeToChatMessageCounter = () => {
    this.unsubscribeFromChatMessageCounter();
    const periodStart = DateTime.local().startOf("month");

    this.chatMessageCounterSubscription = liveQuery(async () => {
      // liveQuery tracks Dexie queries made within async functions, so calling
      // countMyChatMessagesSentSince works correctly and will react to database changes
      return await this.store.sessionEvents.countMyChatMessagesSentSince(periodStart);
    }).subscribe({
      next: count => {
        runInAction(() => {
          this._chatMessageCount = count;
        });
      },
    });
  };

  unsubscribeFromChatMessageCounter = () => {
    this.chatMessageCounterSubscription?.unsubscribe();
    this.chatMessageCounterSubscription = undefined;
  };

  subscribeToNativeChatMessageCounter = () => {
    this.unsubscribeFromNativeChatMessageCounter();
    const periodStart = DateTime.local().startOf("month");
    this._nativeChatMessageCountLoaded = false;

    // Instrumentation only (MEM-19159): a counter that never emits leaves
    // the message gate permanently "not ready", which silently disables PTT
    // and the composer. Dexie swallows DatabaseClosedError without calling
    // the error handler (the query goes dormant), so a watchdog is the only
    // reliable detector of that state.
    this.nativeChatMessageCounterWatchdogTimer = setTimeout(() => {
      this.nativeChatMessageCounterWatchdogTimer = undefined;
      if (this._nativeChatMessageCountLoaded) return;
      logger.warn({
        message: "[AppStoreFeatureGatesStore] native-chat usage counter still not loaded",
        info: { waitedMs: NATIVE_CHAT_COUNTER_WATCHDOG_MS },
      });
    }, NATIVE_CHAT_COUNTER_WATCHDOG_MS);

    this.nativeChatMessageCounterSubscription = liveQuery(async () => {
      return await this.store.sessionEvents.countMyNativeChatMessagesSentSince(periodStart);
    }).subscribe({
      next: count => {
        runInAction(() => {
          this._nativeChatMessageCount = count;
          this._nativeChatMessageCountLoaded = true;
        });
      },
      error: error => {
        logger.warn({
          message: "[AppStoreFeatureGatesStore] native-chat usage counter errored",
          info: { error: String(error) },
        });
      },
    });
  };

  unsubscribeFromNativeChatMessageCounter = () => {
    if (this.nativeChatMessageCounterWatchdogTimer !== undefined) {
      clearTimeout(this.nativeChatMessageCounterWatchdogTimer);
      this.nativeChatMessageCounterWatchdogTimer = undefined;
    }
    this.nativeChatMessageCounterSubscription?.unsubscribe();
    this.nativeChatMessageCounterSubscription = undefined;
  };

  private subscribeToPdfPagesProcessedCounter = () => {
    this.unsubscribeFromPdfPagesProcessedCounter();
    const periodStart = DateTime.local().startOf("month");

    this.pdfPagesProcessedCounterSubscription = liveQuery(async () => {
      return await this.store.spaceAccountNoteAttachments.countPdfPagesProcessedSince(periodStart);
    }).subscribe({
      next: count => {
        runInAction(() => {
          this._pdfPagesProcessedCount = count;
        });
      },
    });
  };

  private unsubscribeFromPdfPagesProcessedCounter = () => {
    this.pdfPagesProcessedCounterSubscription?.unsubscribe();
    this.pdfPagesProcessedCounterSubscription = undefined;
  };

  useInitializeAppStoreEffects() {
    useEffectOnMount(() => {
      this.refreshUpsellBanner();
      const reactionDisposers = [
        reaction(
          () => ({
            queuesAreActive: this.store.notes.queuesAreActive,
            noteCreationCount: this.noteCreationCount,
            chatMessageCount: this.chatMessageCount,
            nativeChatMessageCount: this.nativeChatMessageCount,
            pdfPagesProcessedCount: this.pdfPagesProcessedCount,
            featureAccessTier: this.store.subscriptions.featureAccess?.tier,
            featureAccessPlanKind: this.store.subscriptions.featureAccess?.planKind,
            featureAccessIsPaid: this.store.subscriptions.featureAccess?.isPaid,
            featureAccessIsProactive: this.store.subscriptions.featureAccess?.isProactive,
            memAgentTierOverride:
              this.store.spaceAccounts.myPersonalSpaceAccount?.memAgentTierOverride,
          }),
          () => this.refreshUpsellBanner()
        ),
      ];
      return () => reactionDisposers.forEach(f => f());
    });
  }

  private getGateConfig(gateId: GatedFeature): GatedFeatureConfig {
    const storeConfig = this.store.platformInfo?.getFeatureGateConfig?.(gateId);
    if (!storeConfig) return GATED_FEATURE_DEFAULTS[gateId] ?? UNKNOWN_GATE_FALLBACK;

    return buildFeatureGateConfigFromData(storeConfig, gateId);
  }

  private resolveFeatureLimitKey({
    gateId,
    limits,
    featureAccess,
  }: {
    gateId: GatedFeature;
    limits: FeatureLimits;
    featureAccess: FeatureAccessObservable | undefined;
  }): FeatureLimit {
    if (gateId === GatedFeature.NativeChatMessageSend) {
      const override = this.store.spaceAccounts.myPersonalSpaceAccount?.memAgentTierOverride;
      if (override === "FREE") return FeatureLimit.Free;
      if (override === "PRO") return resolveProFeatureLimitKey(limits);
      if (override === "PROACTIVE") return resolveProactiveFeatureLimitKey(limits);
    }

    if (!featureAccess) return FeatureLimit.Paid;

    if (featureAccess.planKind === SubscriptionPlanKind.FREE) {
      return FeatureLimit.Free;
    }

    if (featureAccess.planKind === SubscriptionPlanKind.PROACTIVE) {
      return resolveProactiveFeatureLimitKey(limits);
    }

    if (featureAccess.planKind === SubscriptionPlanKind.PRO) {
      return resolveProFeatureLimitKey(limits);
    }

    return featureAccess.isPaid ? FeatureLimit.Paid : FeatureLimit.Free;
  }

  checkGate(gateId: GatedFeature): {
    allowed: boolean;
    used: number;
    limit: number;
    warnAt: number | null;
  } {
    const featureAccess = this.store.subscriptions.featureAccess;
    const { limits, period, counter } = this.getGateConfig(gateId);
    const featureLimitKey = this.resolveFeatureLimitKey({ gateId, limits, featureAccess });
    const planLimit = limits[featureLimitKey];

    const { periodStart, periodEnd } = getPeriodWindow(period, DateTime.local());
    const used = counter({
      store: this.store,
      periodStart,
      periodEnd,
    });

    if (planLimit == null) {
      // no plan limit means unlimited
      return { allowed: true, used, limit: Infinity, warnAt: null };
    }

    return { allowed: used < planLimit, used, limit: planLimit, warnAt: limits.warning ?? null };
  }

  openNoteCreationUpsell(params: { used: number; limit: number }) {
    // Delegate to generic upsell
    this.openUsageLimitUpsell({
      ...params,
      feature: GatedFeature.NoteCreate,
      level: "exceeded",
    });
  }

  openChatMessageUpsell(params: { used: number; limit: number }) {
    // Delegate to generic upsell
    this.openUsageLimitUpsell({
      ...params,
      feature: GatedFeature.ChatMessageSend,
      level: "exceeded",
    });
  }

  openUsageLimitUpsell(params: {
    used: number;
    limit: number;
    feature: GatedFeature | undefined;
    context?: PaywallContext;
    level?: FeatureGateLevel;
  }) {
    this.store.subscriptions.openUsageLimitUpsell({
      ...params,
      level: params.level ?? "exceeded",
    });
  }

  closeUpsell = () => {
    this.store.subscriptions.closeUpsell();
  };

  /**
   * Computes and caches the banner visibility and exceeded state for note creation usage.
   */
  refreshUpsellBanner() {
    const notesResult = this.checkGate(GatedFeature.NoteCreate);
    const chatResult = this.checkGate(GatedFeature.ChatMessageSend);
    const nativeChatResult = this.checkGate(GatedFeature.NativeChatMessageSend);

    const pdfResult = this.checkGate(GatedFeature.ContentUnderstandingPdfPage);

    const notesWarn = Boolean(notesResult.warnAt && notesResult.used >= notesResult.warnAt);
    const chatWarn = Boolean(chatResult.warnAt && chatResult.used >= chatResult.warnAt);
    const nativeChatWarn = Boolean(
      nativeChatResult.warnAt && nativeChatResult.used >= nativeChatResult.warnAt
    );
    const pdfWarn = Boolean(pdfResult.warnAt && pdfResult.used >= pdfResult.warnAt);

    const notesExceeded = notesResult.used >= notesResult.limit;
    const chatExceeded = chatResult.used >= chatResult.limit;
    const nativeChatExceeded = nativeChatResult.used >= nativeChatResult.limit;
    const pdfExceeded = pdfResult.used >= pdfResult.limit;

    const isVisible =
      notesWarn ||
      chatWarn ||
      nativeChatWarn ||
      pdfWarn ||
      notesExceeded ||
      chatExceeded ||
      nativeChatExceeded ||
      pdfExceeded;

    runInAction(() => {
      this.upsellBannerVisible = isVisible;
      this.upsellBannerExceeded =
        notesExceeded || chatExceeded || nativeChatExceeded || pdfExceeded;
    });
    if (!isVisible) {
      this.store.subscriptions.closeUpsell();
    }
  }
}

function hasFeatureLimitKey(limits: FeatureLimits, featureLimit: FeatureLimit): boolean {
  return Object.prototype.hasOwnProperty.call(limits, featureLimit);
}

function resolveProFeatureLimitKey(limits: FeatureLimits): FeatureLimit {
  if (hasFeatureLimitKey(limits, FeatureLimit.Pro)) return FeatureLimit.Pro;
  return FeatureLimit.Paid;
}

function resolveProactiveFeatureLimitKey(limits: FeatureLimits): FeatureLimit {
  if (hasFeatureLimitKey(limits, FeatureLimit.Proactive)) return FeatureLimit.Proactive;
  if (hasFeatureLimitKey(limits, FeatureLimit.Pro)) return FeatureLimit.Pro;
  return FeatureLimit.Paid;
}
